/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';

import UploadIcon from '@atlaskit/icon/core/upload';
import Tooltip from '@atlaskit/tooltip';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { useConversationFiles } from '../../../controllers/conversation-files';
import { MAX_FILES } from '../../../controllers/conversation-files/constants';
import { MenuItem } from '../menu-item';

import messages from './messages';

export const FileUploadDropdownItem = ({
	onClick,
	assistanceServiceParams,
	attachmentsStoreKey,
}: {
	onClick: () => void;
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
	attachmentsStoreKey: string;
}) => {
	di(useConversationFiles);
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const { files } = useConversationFiles({
		assistanceServiceParams: {
			...assistanceServiceParams,
			touchpointSource: 'fileUploadButton',
		},
		key: attachmentsStoreKey,
	});
	const isMaxFilesReached = files.length >= MAX_FILES;

	const handleClick = () => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'chatFileUploadButton',
		});

		onClick();
	};

	const menuItem = (
		<MenuItem
			icon={<UploadIcon label="" />}
			label={formatMessage(messages.fileUploadButtonLabel)}
			isDisabled={isMaxFilesReached}
			onClick={handleClick}
			testId="file-upload-dropdown-item"
		/>
	);

	// Wrap with tooltip when disabled to show the max files message
	if (isMaxFilesReached) {
		return (
			<Tooltip
				content={formatMessage(messages.fileUploadButtonMaxFilesLabel, {
					maxFiles: MAX_FILES,
				})}
			>
				{menuItem}
			</Tooltip>
		);
	}

	return menuItem;
};
